/**
 * Earn → Rewards: volume rewards (VOLUME_REWARDS_PLAN.md). A summary, one volume ladder per running
 * campaign, limited-time events, the client's rewards (to claim, in use, history) and how volume counts.
 */
import { useState, type CSSProperties } from 'react';
import {
  fmtUsd,
  stepRewardShort,
  VR_CADENCE_LABEL,
  VR_CLASS_LABEL,
  VR_CLASSES,
  VR_KIND_LABEL,
  volumeShort,
  type VolumeRewardsOverview,
  type VrCadence,
  type VrCampaignView,
  type VrKind,
  type VrReward,
  type VrStep,
  type VrStepView,
  type VrWeights,
} from '@platform/shared';
import { cx } from '../../components/ui';
import { api } from '../../lib/api';
import { Link } from '../../lib/router';
import { Glyph } from '../../components/NotificationIcon';
import { AIcon, errMsg } from '../account/kit';
import { btn, HeroPanel, Panel, type Msg } from './shared';

const KIND_TONE: Record<VrKind, string> = {
  cash: 'bg-up-bg text-up',
  bonus: 'bg-accent-bg text-accent-fg',
  rebate: 'bg-warn-bg text-warn',
};

const usd0 = (x: string | number) => {
  const v = Number(x);
  return Number.isInteger(v) ? `$${v.toLocaleString('en-US')}` : fmtUsd(String(x));
};
const day = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
/** "27 Oct, 14:20", in the viewer's time. */
const dayTime = (iso: string | null) => (iso ? `${day(iso)}, ${new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '—');
const nodeLabel = (v: number) => (v >= 1_000_000 ? `${+(v / 1_000_000).toFixed(2)}M` : `${+(v / 1000).toFixed(1)}K`);

/** "Resets in 3d 14h" for repeating campaigns; "17d left" for a welcome window or an event. */
function clockText(c: VrCampaignView): string {
  if (c.upcoming) return `Starts ${day(c.period.start)}`;
  const ms = Math.max(0, Date.parse(c.endsAt) - Date.now());
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms % 86_400_000) / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const left = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  return c.cadence === 'daily' || c.cadence === 'weekly' || c.cadence === 'monthly' ? `Resets in ${left}` : `${left} left`;
}

/** What the claim did, for the notice. */
function claimedText(r: VrReward): string {
  if (r.kind === 'cash') return `${fmtUsd(r.amount)} added to your live account.`;
  if (r.kind === 'bonus') return `${usd0(r.amount)} trading bonus added to your live account. Usable as margin until ${day(r.activeUntil!)}.`;
  return `${r.pct}% fee & spread rebate running until ${day(r.activeUntil!)}, up to ${usd0(r.amount)}.`;
}

function useClaim(onMsg: (m: Msg) => void) {
  const [busy, setBusy] = useState<string | null>(null);
  const claim = async (ids: string[]) => {
    setBusy(ids[0] ?? null);
    const done: VrReward[] = [];
    try {
      for (const id of ids) done.push(await api.post<VrReward>(`/api/volume-rewards/${id}/claim`));
      onMsg({ ok: true, text: done.length === 1 ? claimedText(done[0]!) : `${done.length} rewards claimed.` });
    } catch (e) {
      onMsg({ ok: false, text: done.length ? `${done.length} claimed, then: ${errMsg(e)}` : errMsg(e) });
    } finally {
      setBusy(null);
    }
  };
  return { busy, claim };
}

function ClockPill({ text }: { text: string }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-track px-2.5 py-1 text-[13px] font-medium whitespace-nowrap text-muted">
      <Glyph name="clock" size={14} />
      {text}
    </span>
  );
}

function RewardChip({ step, className }: { step: Pick<VrStep, 'kind' | 'usd' | 'pct'>; className?: string }) {
  return <span className={cx('num inline-flex h-[26px] items-center rounded-[7px] px-2.5 text-[12px] font-semibold whitespace-nowrap', KIND_TONE[step.kind], className)}>{stepRewardShort(step)}</span>;
}

const STATE_TEXT: Partial<Record<VrStepView['state'], string>> = {
  claimed: 'Claimed',
  held: 'Being checked',
  expired: 'Expired',
  missed: 'Not paid',
  locked: 'Not reached',
  budget: 'Budget used up',
};

// ---------- the ladder ----------

/**
 * One list, evenly spaced: horizontal from 640px (reward above each node, state below), vertical on
 * phones. The fill between two steps is to scale.
 */
function Ladder({ c, busy, onClaim }: { c: VrCampaignView; busy: string | null; onClaim: (id: string) => void }) {
  const counted = Number(c.counted);
  let prev = 0;
  const dim = !!c.gate || c.upcoming;
  return (
    <ol className={cx('m-0 grid list-none p-0 sm:grid-cols-[repeat(var(--n),minmax(0,1fr))]', dim && 'opacity-50')} style={{ '--n': c.steps.length } as CSSProperties}>
      {c.steps.map((s, i) => {
        const fill = Math.max(0, Math.min(1, (counted - prev) / (s.step.volume - prev))) * 100;
        prev = s.step.volume;
        const seg = dim ? 'var(--muted)' : 'var(--accent)';
        const rail = (dir: string) => `linear-gradient(${dir}, ${seg} ${fill}%, var(--border-strong) ${fill}%)`;
        return (
          <li key={s.index} className="relative grid grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-x-3.5 py-2.5 sm:flex sm:flex-col sm:items-center sm:gap-2.5 sm:py-0 sm:text-center">
            {i > 0 && <span aria-hidden className="absolute top-[-50%] left-[18px] h-full w-1 rounded sm:hidden" style={{ background: rail('to bottom') }} />}
            <span
              aria-hidden
              className={cx('absolute top-[54px] right-1/2 h-1 rounded max-sm:hidden', i === 0 ? 'w-1/2' : 'w-full')}
              style={{ background: rail('to right') }}
            />
            <RewardChip step={s.step} className="order-3 justify-self-end sm:order-1" />
            <span
              className={cx(
                'num relative z-[1] order-1 grid size-10 place-items-center rounded-full border-2 text-[11px] font-semibold sm:order-2',
                s.state === 'claimed'
                  ? 'border-up bg-up text-bg'
                  : s.state === 'ready'
                    ? 'border-accent bg-accent text-white shadow-[0_0_0_5px_var(--accent-bg)]'
                    : s.state === 'next'
                      ? 'border-accent bg-card text-accent-fg shadow-[0_0_0_4px_var(--accent-bg)]'
                      : 'border-line-strong bg-card text-muted',
              )}
              aria-label={`${volumeShort(s.step.volume)} step: ${s.state === 'next' ? 'next' : (STATE_TEXT[s.state] ?? 'ready to claim').toLowerCase()}`}
            >
              {s.state === 'claimed' ? <AIcon name="check" size={18} /> : nodeLabel(s.step.volume)}
            </span>
            <span className="order-2 flex min-w-0 flex-col items-start gap-0.5 text-[12.5px] leading-tight text-muted sm:order-3 sm:min-h-[30px] sm:items-center">
              <span className="text-[14.5px] font-medium text-fg sm:hidden">{volumeShort(s.step.volume)}</span>
              {s.state === 'ready' && s.rewardId ? (
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => onClaim(s.rewardId!)}
                  className="mt-0.5 inline-flex h-[30px] items-center rounded-lg bg-accent px-3 text-[12.5px] font-semibold whitespace-nowrap text-white disabled:opacity-60"
                >
                  {busy === s.rewardId ? 'Claiming…' : `Claim ${s.step.kind === 'rebate' ? `${s.step.pct}%` : usd0(s.step.usd)}`}
                </button>
              ) : (
                <span className={cx(s.state === 'claimed' && 'font-medium text-up')}>
                  {s.state === 'next' && s.toGo !== null ? `${volumeShort(s.toGo)} to go` : STATE_TEXT[s.state]}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Breakdown({ c }: { c: VrCampaignView }) {
  if (!c.classes.length && !Number(c.excluded)) return null;
  return (
    <details className="group rounded-2xl border border-line px-4">
      <summary className="flex cursor-pointer list-none items-center gap-2 py-3 text-[13.5px] font-semibold [&::-webkit-details-marker]:hidden">
        <span className="num w-3.5 text-muted group-open:hidden">+</span>
        <span className="num hidden w-3.5 text-muted group-open:inline">−</span>
        How this {c.cadence === 'weekly' ? "week's" : c.cadence === 'monthly' ? "month's" : c.cadence === 'daily' ? "day's" : ''} volume counts
      </summary>
      <table className="mb-3 w-full border-collapse text-[13px]">
        <tbody>
          {c.classes.map((x) => (
            <tr key={x.assetClass} className="border-t border-line">
              <td className="py-1.5">
                {VR_CLASS_LABEL[x.assetClass]} · {fmtUsd(x.raw, 0)} at {x.weight}%
              </td>
              <td className="num py-1.5 pl-3 text-right whitespace-nowrap">{fmtUsd(x.counted, 0)}</td>
            </tr>
          ))}
          <tr className="border-t border-line font-semibold">
            <td className="py-1.5">Counted</td>
            <td className="num py-1.5 pl-3 text-right">{fmtUsd(c.counted, 0)}</td>
          </tr>
          {Number(c.excluded) > 0 && (
            <tr className="border-t border-line text-muted">
              <td className="py-1.5">Not counted · closed within {Math.round(c.minHoldSeconds / 60)} minutes, or hedged</td>
              <td className="num py-1.5 pl-3 text-right whitespace-nowrap">{fmtUsd(c.excluded, 0)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </details>
  );
}

function CampaignCard({ c, busy, onClaim }: { c: VrCampaignView; busy: string | null; onClaim: (id: string) => void }) {
  const next = c.steps.find((s) => s.state === 'next');
  const welcome = c.cadence === 'welcome';
  return (
    <Panel
      className={cx(
        welcome && 'border-[color-mix(in_srgb,var(--accent)_45%,var(--border))] bg-[linear-gradient(180deg,color-mix(in_srgb,var(--accent)_7%,var(--card)),var(--card)_55%)]',
      )}
    >
      <div className="flex flex-wrap items-start gap-x-4 gap-y-2.5">
        <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-1">
          <span className="num text-[11.5px] tracking-[.07em] text-muted uppercase">
            {VR_CADENCE_LABEL[c.cadence]}
            {c.symbols.length ? ` · ${c.symbols.join(', ')}` : ''}
          </span>
          <h3 className="m-0 text-[20px] font-semibold md:text-[22px]">{c.name}</h3>
          {c.description && <p className="m-0 text-[14px] text-muted">{c.description}</p>}
        </div>
        {!(welcome && c.gate) && <ClockPill text={clockText(c)} />}
      </div>
      {c.gate ? (
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 rounded-2xl bg-track px-4 py-3 text-[13.5px]">
          <span className="shrink-0 text-muted">
            <Glyph name="lock" size={16} />
          </span>
          <span className="min-w-0 flex-[1_1_220px]">
            {c.gate}.{c.gateVolume ? (
              <>
                {' '}
                Trade <b className="num">{fmtUsd(String(c.gateVolume), 0)}</b> more lifetime volume to join.
              </>
            ) : null}
          </span>
          <Link to={c.gateVolume ? '/earn/benefits' : '/account/deposit'} className={btn('secondary', 'sm')}>
            {c.gateVolume ? 'See tiers' : 'Deposit'}
          </Link>
        </div>
      ) : (
        !c.upcoming && (
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="num text-[28px] font-semibold tracking-tight">{fmtUsd(c.counted, 0)}</span>
            <span className="text-[14px] text-muted">
              counted{next?.toGo ? ` · ${fmtUsd(String(next.toGo), 0)} to the next step` : c.budgetUsed ? " · this period's budget is used up" : ''}
            </span>
          </div>
        )
      )}
      <Ladder c={c} busy={busy} onClaim={onClaim} />
      <Breakdown c={c} />
    </Panel>
  );
}

/** A one-step or upcoming event as a compact card. */
function EventCard({ c, busy, onClaim }: { c: VrCampaignView; busy: string | null; onClaim: (id: string) => void }) {
  const s = c.steps[0]!;
  const counted = Number(c.counted);
  const pct = Math.min(100, (counted / s.step.volume) * 100);
  return (
    <div className={cx('flex min-w-0 flex-col gap-3 rounded-[20px] border p-[18px]', c.upcoming ? 'border-dashed border-line-strong' : 'border-line bg-card')}>
      <div className="flex items-center justify-between gap-2.5">
        <h4 className="m-0 text-[17px] font-semibold">{c.name}</h4>
        <RewardChip step={s.step} className="!h-6" />
      </div>
      <p className="m-0 text-[13.5px] text-muted">
        {c.description || `Trade ${volumeShort(s.step.volume)}${c.symbols.length ? ` in ${c.symbols.join(', ')}` : ''} by ${day(c.endsAt)}.`}
      </p>
      <div role="progressbar" aria-label={`${c.name} progress`} aria-valuemin={0} aria-valuemax={s.step.volume} aria-valuenow={counted} className="h-2 overflow-hidden rounded-full bg-track">
        <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-auto flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1 text-[13px] text-muted">
        <span className="num">{c.upcoming ? `Starts ${dayTime(c.period.start)}` : `${fmtUsd(c.counted, 0)} of ${fmtUsd(String(s.step.volume), 0)}`}</span>
        <span>{c.upcoming ? '' : clockText(c)}</span>
      </div>
      {s.state === 'ready' && s.rewardId ? (
        <button type="button" disabled={busy !== null} onClick={() => onClaim(s.rewardId!)} className="inline-flex h-[30px] items-center self-start rounded-lg bg-accent px-3.5 text-[12.5px] font-semibold text-white disabled:opacity-60">
          {busy === s.rewardId ? 'Claiming…' : 'Claim'}
        </button>
      ) : s.state !== 'next' && s.state !== 'locked' ? (
        <span className={cx('text-[13px] font-medium', s.state === 'claimed' ? 'text-up' : 'text-muted')}>{STATE_TEXT[s.state]}</span>
      ) : !c.upcoming ? (
        <Link to="/trade" className="inline-flex h-[30px] items-center self-start rounded-lg border border-line-strong px-3.5 text-[12.5px] font-semibold text-fg no-underline">
          Trade
        </Link>
      ) : null}
    </div>
  );
}

// ---------- my rewards ----------

type RTab = 'claim' | 'use' | 'history';

const mark = (r: VrReward) => (r.kind === 'rebate' ? `−${r.pct}%` : usd0(r.amount));

function RewardRow({ r, busy, onClaim, canClaim }: { r: VrReward; busy: string | null; onClaim: (id: string) => void; canClaim: boolean }) {
  const ending = r.kind === 'bonus' && r.status === 'active' && r.activeUntil && Date.parse(r.activeUntil) <= Date.now();
  const detail = `${r.campaign} · ${volumeShort(r.stepVolume)} step`;
  let right: React.ReactNode;
  switch (r.status) {
    case 'ready':
      right = (
        <>
          <button
            type="button"
            disabled={busy !== null || !canClaim}
            onClick={() => onClaim(r.id)}
            className="inline-flex h-[30px] items-center rounded-lg bg-accent px-3.5 text-[12.5px] font-semibold text-white disabled:opacity-50"
          >
            {busy === r.id ? 'Claiming…' : 'Claim'}
          </button>
          <span>Claim by {day(r.claimBy)}</span>
        </>
      );
      break;
    case 'held':
      right = <span>Being checked</span>;
      break;
    case 'active':
      right =
        r.kind === 'rebate' ? (
          <>
            <b className="num text-[14px] text-fg">
              {fmtUsd(r.paid)} of {usd0(r.amount)}
            </b>
            <span>Until {dayTime(r.activeUntil)}</span>
          </>
        ) : ending ? (
          <>
            <b className="num text-[14px] text-fg">{fmtUsd(r.remaining)}</b>
            <span>Ending: backs open positions</span>
          </>
        ) : (
          <>
            <b className="num text-[14px] text-fg">{fmtUsd(r.remaining)}</b>
            <span>Until {dayTime(r.activeUntil)}</span>
          </>
        );
      break;
    case 'finished':
      right = (
        <>
          <b className="num text-[14px] text-fg">{r.kind === 'rebate' ? `+${fmtUsd(r.paid)}` : r.kind === 'cash' ? `+${fmtUsd(r.amount)}` : usd0(r.amount)}</b>
          <span>{r.kind === 'cash' ? `Claimed ${day(r.claimedAt!)}` : `Ended ${day(r.activeUntil ?? r.claimedAt!)}`}</span>
        </>
      );
      break;
    case 'expired':
      right = (
        <>
          <b className="text-[14px] text-fg">Expired</b>
          <span>Not claimed by {day(r.claimBy)}</span>
        </>
      );
      break;
    default:
      right = <b className="text-[14px] text-fg">{r.status === 'reversed' ? 'Reversed' : 'Not paid'}</b>;
  }
  const gone = r.status === 'expired' || r.status === 'rejected' || r.status === 'reversed';
  return (
    <div className={cx('grid grid-cols-[44px_minmax(0,1fr)] items-center gap-x-3.5 gap-y-1 border-b border-line py-3.5 last:border-b-0 sm:grid-cols-[48px_minmax(0,1fr)_auto]', gone && 'opacity-60')}>
      <span className={cx('num grid size-11 place-items-center rounded-full text-[12px] font-semibold sm:size-12 sm:text-[13px]', KIND_TONE[r.kind])}>{mark(r)}</span>
      <div className="min-w-0">
        <div className="text-[15px] font-medium">{VR_KIND_LABEL[r.kind]}</div>
        <div className="text-[13px] text-muted">
          {detail}
          {r.kind === 'bonus' && r.status === 'active' ? " · margin only, can't be withdrawn" : r.kind === 'rebate' && r.status === 'active' ? ' · paid as cashback you claim' : ''}
        </div>
        {r.kind === 'rebate' && r.status === 'active' && (
          <div aria-hidden className="mt-1.5 h-[5px] overflow-hidden rounded-full bg-track">
            <div className="h-full rounded-full bg-warn" style={{ width: `${Math.min(100, (Number(r.paid ?? 0) / Number(r.amount)) * 100)}%` }} />
          </div>
        )}
      </div>
      <div className="col-start-2 flex flex-wrap items-center gap-2 text-[12.5px] text-muted sm:col-start-3 sm:flex-col sm:items-end sm:gap-1 sm:text-right">{right}</div>
    </div>
  );
}

function MyRewards({ data, busy, onClaim }: { data: VolumeRewardsOverview; busy: string | null; onClaim: (id: string) => void }) {
  const groups: Record<RTab, VrReward[]> = {
    claim: data.rewards.filter((r) => r.status === 'ready' || r.status === 'held'),
    use: data.rewards.filter((r) => r.status === 'active'),
    history: data.rewards.filter((r) => r.status !== 'ready' && r.status !== 'held' && r.status !== 'active'),
  };
  const [tab, setTab] = useState<RTab>(groups.claim.length ? 'claim' : groups.use.length ? 'use' : 'history');
  const tabs: [RTab, string][] = [
    ['claim', `To claim${groups.claim.length ? ` · ${groups.claim.length}` : ''}`],
    ['use', `In use${groups.use.length ? ` · ${groups.use.length}` : ''}`],
    ['history', 'History'],
  ];
  const nextUp = data.campaigns.flatMap((c) => c.steps.filter((s) => s.state === 'next').map((s) => ({ c, s })))[0];
  return (
    <Panel title="My rewards" icon="wallet">
      <div role="tablist" aria-label="My rewards" className="no-scrollbar flex max-w-full gap-1 self-start overflow-x-auto rounded-full bg-track p-1">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={cx('shrink-0 rounded-full px-3.5 py-2 text-[13.5px] font-semibold whitespace-nowrap', tab === id ? 'bg-card text-fg shadow-sm' : 'text-muted hover:text-fg')}
          >
            {label}
          </button>
        ))}
      </div>
      <div role="tabpanel" className="rounded-[20px] border border-line px-[18px]">
        {groups[tab].length ? (
          groups[tab].map((r) => <RewardRow key={r.id} r={r} busy={busy} onClaim={onClaim} canClaim={data.claimBlock === null} />)
        ) : (
          <p className="m-0 px-1 py-7 text-center text-[14px] text-muted">
            {tab === 'claim'
              ? nextUp
                ? `Nothing to claim right now. Next up: ${stepRewardShort(nextUp.s.step)} at ${volumeShort(nextUp.s.step.volume)} in ${nextUp.c.name}.`
                : 'Nothing to claim right now.'
              : tab === 'use'
                ? 'No trading bonus or rebate running.'
                : 'Rewards you claim, and ones that expire, show here.'}
          </p>
        )}
      </div>
    </Panel>
  );
}

// ---------- summary and rules ----------

function Summary({ data, busy, onClaimAll }: { data: VolumeRewardsOverview; busy: string | null; onClaimAll: () => void }) {
  const s = data.summary;
  const tiles: { label: string; value: string; sub: React.ReactNode; tone?: string }[] = [
    {
      label: 'Ready to claim',
      value: fmtUsd(s.ready),
      tone: 'text-accent-fg',
      sub:
        s.readyCount === 0 ? (
          'Nothing waiting'
        ) : data.claimBlock === 'kyc' ? (
          <Link to="/account/kyc" className="font-semibold text-accent-fg">
            Verify to claim
          </Link>
        ) : data.claimBlock === 'blocked' ? (
          'Not available on your account'
        ) : (
          <button type="button" disabled={busy !== null} onClick={onClaimAll} className={cx(btn('primary', 'sm'), 'mt-1 self-start')}>
            {busy ? 'Claiming…' : s.readyCount === 1 ? 'Claim' : `Claim all ${s.readyCount}`}
          </button>
        ),
    },
    {
      label: 'Trading bonus in use',
      value: fmtUsd(s.bonus),
      sub: s.bonusCount === 0 ? 'None running' : s.bonusEndsAt ? `${s.bonusCount > 1 ? `${s.bonusCount} bonuses · next ends` : 'Ends'} ${dayTime(s.bonusEndsAt)}` : '',
    },
    {
      label: 'Rebate running',
      value: s.rebate ? `${s.rebate.pct}%` : '—',
      sub: s.rebate ? `${fmtUsd(s.rebate.paid)} back of ${usd0(s.rebate.cap)} · ends ${day(s.rebate.endsAt)}` : 'None running',
    },
    { label: `Earned in ${new Date().toLocaleDateString('en-GB', { month: 'long' })}`, value: fmtUsd(s.earnedMonth), sub: 'Cash, bonus and rebate' },
  ];
  return (
    <HeroPanel glow="70% 130%">
      <h2 className="m-0 text-[24px] font-semibold md:text-[30px]">Trade more, unlock cash and bonuses</h2>
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        {tiles.map((t, i) => (
          <div key={t.label} className={cx('flex min-w-0 flex-col gap-1 rounded-[18px] border bg-card/70 p-3.5 sm:px-[18px] sm:py-4', i === 0 ? 'border-accent/50' : 'border-line')}>
            <span className="text-[12.5px] text-muted sm:text-[13px]">{t.label}</span>
            <span className={cx('num text-[20px] font-semibold tracking-tight sm:text-[24px]', t.tone)}>{t.value}</span>
            <span className="text-[12.5px] text-muted">{t.sub}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[13px] text-muted">
        <span>Counted volume</span>
        {(
          [
            ['Today', data.volume.today],
            ['This week', data.volume.week],
            [new Date().toLocaleDateString('en-GB', { month: 'long' }), data.volume.month],
          ] as const
        ).map(([l, v]) => (
          <span key={l} className="inline-flex items-baseline gap-1.5 rounded-full border border-line-strong bg-card/60 px-3 py-1">
            {l} <b className="num font-semibold text-fg">{fmtUsd(v, 0)}</b>
          </span>
        ))}
      </div>
    </HeroPanel>
  );
}

/** "Crypto counts in full. Stocks count at 50%, indices at 40%, commodities at 40% and FX at 20%. Meme coins don't count." */
function weightsText(w: VrWeights): string {
  const list = (xs: string[]) => (xs.length > 1 ? `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}` : (xs[0] ?? ''));
  const low = (k: (typeof VR_CLASSES)[number]) => (k === 'FX' ? 'FX' : VR_CLASS_LABEL[k].toLowerCase());
  const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);
  const singular = (ks: string[]) => ks.length === 1 && ks[0] === 'CRYPTO';
  const full = VR_CLASSES.filter((k) => w[k] >= 100);
  const part = VR_CLASSES.filter((k) => w[k] > 0 && w[k] < 100);
  const none = VR_CLASSES.filter((k) => !(w[k] > 0));
  const out: string[] = [];
  if (full.length) out.push(`${cap(list(full.map(low)))} ${singular(full) ? 'counts' : 'count'} in full.`);
  if (part.length) out.push(`${cap(list(part.map((k, i) => `${low(k)}${i === 0 ? (singular([k]) ? ' counts' : ' count') : ''} at ${w[k]}%`)))}.`);
  if (none.length) out.push(`${cap(list(none.map(low)))} ${singular(none) ? "doesn't" : "don't"} count.`);
  return out.join(' ');
}

function Rules({ data }: { data: VolumeRewardsOverview }) {
  const rules = [
    'Trades on your live account count. Demo and copy trades don’t.',
    'Every open and every close counts at its full size: margin × leverage.',
    `A position closed within ${Math.round(data.minHoldSeconds / 60)} minutes of opening doesn’t count.`,
    'A buy and a sell held together on the same market don’t count.',
    weightsText(data.weights),
    'Verify your identity to claim. Your progress counts before that.',
    'Claim within the days shown on each reward. Unclaimed rewards expire.',
    'A trading bonus is margin only and can’t be withdrawn. Claiming one needs at least that much of your own money in your live account, and requesting a withdrawal removes it.',
  ];
  return (
    <Panel title="How volume counts" icon="info">
      <ul className="m-0 grid list-none gap-x-7 gap-y-2.5 p-0 text-[13.5px] text-muted md:grid-cols-2">
        {rules.map((r) => (
          <li key={r} className="grid grid-cols-[16px_minmax(0,1fr)] gap-2.5">
            <span aria-hidden className="mt-[7px] size-1.5 justify-self-center rounded-full bg-line-strong" />
            {r}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

// ---------- the tab ----------

const ORDER: VrCadence[] = ['welcome', 'daily', 'weekly', 'monthly', 'event'];
type Filter = 'all' | VrCadence;

export function RewardsTab({ data, onMsg }: { data: VolumeRewardsOverview; onMsg: (m: Msg) => void }) {
  const { busy, claim } = useClaim(onMsg);
  const [filter, setFilter] = useState<Filter>('all');
  const onClaim = (id: string) => void claim([id]);
  const isEventCard = (c: VrCampaignView) => c.cadence === 'event' && (c.upcoming || c.steps.length === 1);
  const counts = new Map<VrCadence, number>();
  for (const c of data.campaigns) counts.set(c.cadence, (counts.get(c.cadence) ?? 0) + 1);
  const shown = data.campaigns.filter((c) => filter === 'all' || c.cadence === filter);
  const ladders = shown.filter((c) => !isEventCard(c));
  const events = shown.filter(isEventCard);
  const filters: [Filter, string, number][] = [['all', 'All', data.campaigns.length], ...ORDER.filter((k) => counts.has(k)).map((k): [Filter, string, number] => [k, k === 'event' ? 'Events' : VR_CADENCE_LABEL[k], counts.get(k)!])];

  return (
    <>
      <Summary data={data} busy={busy} onClaimAll={() => void claim(data.rewards.filter((r) => r.status === 'ready').map((r) => r.id))} />
      {data.campaigns.length > 0 ? (
        filters.length > 2 && (
          <div role="group" aria-label="Show" className="flex flex-wrap gap-1.5">
            {filters.map(([id, label, n]) => (
              <button
                key={id}
                type="button"
                aria-pressed={filter === id}
                onClick={() => setFilter(id)}
                className={cx(
                  'rounded-full border px-4 py-2 text-[13.5px] font-semibold',
                  filter === id ? 'border-fg bg-fg text-bg' : 'border-line-strong text-muted hover:text-fg',
                )}
              >
                {label}
                <span className="num ml-1 font-medium opacity-70">{n}</span>
              </button>
            ))}
          </div>
        )
      ) : (
        <Panel>
          <p className="m-0 py-8 text-center text-[15px] text-muted">No volume campaigns are running right now. Your rewards stay below.</p>
        </Panel>
      )}
      {ladders.map((c) => (
        <CampaignCard key={c.id} c={c} busy={busy} onClaim={onClaim} />
      ))}
      {events.length > 0 && (
        <Panel title="Events" aside={<span className="rounded-md bg-track px-2.5 py-1 text-[12.5px] font-medium text-muted">Limited time</span>}>
          <div className="grid gap-4 md:grid-cols-2">
            {events.map((c) => (
              <EventCard key={c.id} c={c} busy={busy} onClaim={onClaim} />
            ))}
          </div>
        </Panel>
      )}
      <MyRewards data={data} busy={busy} onClaim={onClaim} />
      <Rules data={data} />
    </>
  );
}

/** Earn overview: the next volume step in one line, for the "More ways to earn" list. */
export function volumeTeaser(data: VolumeRewardsOverview): { title: string; text: string } {
  const c = data.campaigns.find((x) => x.cadence === 'weekly' && !x.gate) ?? data.campaigns.find((x) => !x.gate && !x.upcoming);
  const next = c?.steps.find((st) => st.state === 'next');
  if (data.summary.readyCount > 0) return { title: 'Volume rewards', text: `${data.summary.readyCount} ${data.summary.readyCount === 1 ? 'reward' : 'rewards'} ready to claim.` };
  if (!c) return { title: 'Volume rewards', text: 'Trade to earn USDT, trading bonuses and rebates.' };
  return {
    title: c.name,
    text: next ? `${volumeShort(next.toGo ?? 0)} more volume for ${stepRewardShort(next.step)} · ${clockText(c).toLowerCase()}.` : 'Every step reached for this period.',
  };
}
